import {TimeNavigationType} from "@/shared/timrTypes";
import {formatDate, getStartOfConfiguredWeek, getStartOfQuarter, isSameDay, parseDateInput, Temporal} from "@/shared/utils/datefunctions";

export type TimeSpanChangedHandler = (dateFrom: Temporal.PlainDate | null, dateTo: Temporal.PlainDate | null, currentType: TimeNavigationType) => void;

export interface TimeNavigationBarConfig {
	container: JQuery;
	timeSpanChanged: TimeSpanChangedHandler;
	defaultDateType: TimeNavigationType;
	defaultFromDate: Temporal.PlainDate | null;
	defaultToDate: Temporal.PlainDate | null;
	disableDateFields?: boolean; // disable the use of the date input fields
}

/**
 * Handles time navigation bar to filter based on a date range
 *
 * Important: Not all types (TimeNavigationType) may be available, as they can be configured by passing an individual set of types to the template (TimeNavigationBar.jsp)
 * This variable is "customTimeNavigationBarTypes" and has to be transmitted as ModelAndView variable from the controller
 * The reason for this is that it flickers if it would be shown or hidden via js code, as this takes longer than showing the template
 */
export class TimeNavigationBar {

	private currentType: TimeNavigationType;
	private currentDateFrom: Temporal.PlainDate | null;
	private currentDateTo: Temporal.PlainDate | null;

	constructor(private readonly config: TimeNavigationBarConfig) {
		this.currentType = config.defaultDateType;
		this.currentDateFrom = config.defaultFromDate;
		this.currentDateTo = config.defaultToDate;
		this.init();
		this.initTimeSpan();
	}

	public setTimeSpan(dateFrom: Temporal.PlainDate | null, dateTo: Temporal.PlainDate | null, type: TimeNavigationType) {
		this.setTimeNavigationType(type);
		this.currentDateFrom = dateFrom;
		this.currentDateTo = dateTo;
		this.displayTimeSpan();
	}

	public getDateFrom(): Temporal.PlainDate | null {
		return this.currentDateFrom;
	}

	public getDateTo(): Temporal.PlainDate | null {
		return this.currentDateTo;
	}

	public getTimeNavigationType(): TimeNavigationType {
		return this.currentType;
	}

	private init() {
		this.currentType = this.config.defaultDateType;
		this.config.container.find(".time-navigation-pills > li > a").click(e => {
			const type = this.parseType($(e.target));
			this.onTypeButtonClicked(type);
		});
		this.config.container.find(".time-navigation-previous").click(() => {
			this.gotoPreviousDate();
		});
		this.config.container.find(".time-navigation-next").click(() => {
			this.gotoNextDate();
		});
		this.config.container.find(".time-navigation-from").change(() => {
			this.onTimeInputsChanged();
		});
		this.config.container.find(".time-navigation-to").change(() => {
			this.onTimeInputsChanged();
		});

		this.config.container.find(".focusOnStartDateField").click(() => {
			this.config.container.find(".time-navigation-from").focus();
		});

		this.config.container.find(".focusOnEndDateField").click(() => {
			this.config.container.find(".time-navigation-to").focus();
		});

		if (this.config.disableDateFields) {
			this.config.container.find(".dateRangeInputGroup, .singleDateInputGroup")
				.find("input, button")
				.prop("disabled", true);
		}
	}

	private initTimeSpan() {
		if (this.currentType !== TimeNavigationType.Total && !this.config.defaultFromDate) {
			this.currentDateFrom = getStartOfConfiguredWeek(Temporal.Now.plainDateISO());
			this.setTimeNavigationType(TimeNavigationType.Week);
		} else {
			if (this.currentType !== TimeNavigationType.Total) {
				this.currentDateFrom = this.config.defaultFromDate;
			}
			this.setTimeNavigationType(this.config.defaultDateType);
		}
		if (this.currentType === TimeNavigationType.Custom && this.config.defaultToDate) {
			this.currentDateTo = this.config.defaultToDate;
		}
		if (this.currentType !== TimeNavigationType.Custom) {
			this.currentDateTo = this.calculateDateTo(this.currentDateFrom);
		}

		this.displayTimeSpan();
	}

	private calculateDateTo(from: Temporal.PlainDate | null): Temporal.PlainDate | null {
		if (!from) {
			return null;
		}
		return this.addTimespan(from, 1)?.subtract({days: 1}) ?? null;
	}

	private addTimespan(date: Temporal.PlainDate, amount: number): Temporal.PlainDate | null {
		switch (this.currentType) {
			case TimeNavigationType.Day:
				return date.add({days: amount});
			case TimeNavigationType.Week:
				return date.add({days: amount * 7});
			case TimeNavigationType.Month:
				return date.add({months: amount});
			case TimeNavigationType.Quarter:
				return date.add({months: amount * 3});
			case TimeNavigationType.Year:
				return date.add({years: amount});
			default:
				return null;
		}
	}

	private setTimeNavigationType(type: TimeNavigationType) {
		this.config.container.find(".time-navigation-pills > li > a[data-type='" + type + "']").tab("show");
		this.currentType = type;
	}

	private displayTimeSpan() {
		const showDate = this.currentType !== TimeNavigationType.Total;
		this.config.container.find(".time-navigation-controls").toggle(showDate);

		if (showDate) {
			this.config.container.find(".time-navigation-from").val(formatDate(this.currentDateFrom));
			if (this.currentType !== TimeNavigationType.Day) {
				this.config.container.find(".time-navigation-to").val(formatDate(this.currentDateTo));
			}

			this.config.container.find(".singleDateInputGroup").toggle(this.currentType === TimeNavigationType.Day);
			this.config.container.find(".dateRangeInputGroup").toggle(this.currentType !== TimeNavigationType.Day);
			this.config.container.find(".shiftDateInputGroup").toggle(this.currentType !== TimeNavigationType.Custom);
		}
	}

	private onTimeInputsChanged() {
		const from = parseDateInput(this.config.container.find(".time-navigation-from:visible").val() as string);
		const to = parseDateInput(this.config.container.find(".time-navigation-to:visible").val() as string);

		// valid means: input dates are given/valid for current range type e.g. 'Custom'
		if (!this.isDateRangeValid(from, to)) {
			return;
		}

		// changed means: at least one relevant date differs from current state
		if (!this.hasInputDateChanged(from, to)) {
			return;
		}

		this.applyTimeInputChange(from, to);
		this.displayTimeSpan();
		this.onTimeSpanChanged();
		this.setTimeNavigationType(this.currentType);
	}

	private parseType(anchorElement: JQuery): TimeNavigationType {
		return anchorElement.data("type") as TimeNavigationType;
	}

	private gotoNextDate() {
		if (!this.currentDateFrom) {
			return;
		}
		this.currentDateFrom = this.addTimespan(this.currentDateFrom, 1);
		this.currentDateTo = this.calculateDateTo(this.currentDateFrom);
		this.displayTimeSpan();
		this.onTimeSpanChanged();
	}

	private gotoPreviousDate() {
		if (!this.currentDateFrom) {
			return;
		}
		this.currentDateFrom = this.addTimespan(this.currentDateFrom, -1);
		this.currentDateTo = this.calculateDateTo(this.currentDateFrom);
		this.displayTimeSpan();
		this.onTimeSpanChanged();
	}

	private onTypeButtonClicked(type: TimeNavigationType) {
		if (type !== this.currentType) {
			this.currentType = type;
			const today = Temporal.Now.plainDateISO();
			switch (this.currentType) {
				case TimeNavigationType.Day:
					this.currentDateFrom = today;
					break;
				case TimeNavigationType.Week:
					this.currentDateFrom = getStartOfConfiguredWeek(today);
					break;
				case TimeNavigationType.Month:
					this.currentDateFrom = today.with({day: 1});
					break;
				case TimeNavigationType.Quarter:
					this.currentDateFrom = getStartOfQuarter(today);
					break;
				case TimeNavigationType.Year:
					this.currentDateFrom = today.with({month: 1, day: 1});
					break;
				case TimeNavigationType.Total:
					this.currentDateFrom = null;
					break;
			}
			if (this.currentType !== TimeNavigationType.Custom) {
				this.currentDateTo = this.calculateDateTo(this.currentDateFrom);
			}
			this.displayTimeSpan();
			this.onTimeSpanChanged();
		}
	}

	private onTimeSpanChanged() {
		this.config.timeSpanChanged(this.currentDateFrom, this.currentDateTo, this.currentType);
	}

	/**
	 * Checks whether parsed input values would effectively change the current state.
	 * A bound participates only when its parsed value is non-null; null indicates no applicable value.
	 */
	private hasInputDateChanged(from: Temporal.PlainDate | null, to: Temporal.PlainDate | null): boolean {
		return from !== null && !isSameDay(this.currentDateFrom, from) ||
			to !== null && !isSameDay(this.currentDateTo, to);
	}

	/**
	 * Validates the currently entered date inputs for the active time-navigation type.
	 * This is an input-level guard that decides whether a change event may be applied,
	 * not a full semantic validation of an end-to-end reporting period.
	 */
	private isDateRangeValid(from: Temporal.PlainDate | null, to: Temporal.PlainDate | null): boolean {
		// Total: date inputs do not matter
		if (this.currentType === TimeNavigationType.Total) {
			return true;
		}

		// Day: only the "from" side is relevant for a valid change.
		if (this.currentType === TimeNavigationType.Day) {
			return from !== null;
		}

		// Week/Month/Quarter/Year: allow progressing if both bounds are valid.
		if (this.currentType !== TimeNavigationType.Custom) {
			return from !== null && to !== null;
		}

		// Custom: allow progressing independent of date inputs
		return true;
	}

	/**
	 * Applies parsed input values and restores time-navigation invariants:
	 * - manual edits in predefined ranges switch to custom (except day/custom)
	 * - day mode always derives "to" from "from"
	 */
	private applyTimeInputChange(from: Temporal.PlainDate | null, to: Temporal.PlainDate | null): void {
		this.currentDateFrom = from;
		if (this.isRangeMode()) {
			this.currentDateTo = to;
		}

		if (this.currentType !== TimeNavigationType.Day && this.currentType !== TimeNavigationType.Custom) {
			this.currentType = TimeNavigationType.Custom;
		}
		if (this.currentType === TimeNavigationType.Day) {
			this.currentDateTo = this.calculateDateTo(this.currentDateFrom);
		}
	}

	/**
	 * Determines if the current type is in range mode.
	 * This checks if the `currentType` is neither `Day` nor `Total` from the `TimeNavigationType`.
	 *
	 * @return {boolean} Returns `true` if the current type is in range mode; otherwise, returns `false`.
	 */
	private isRangeMode(): boolean {
		return this.currentType !== TimeNavigationType.Day && this.currentType !== TimeNavigationType.Total;
	}

}
